"use client";

import { usePathname, useSearchParams } from "next/navigation";
import { useEffect, useRef } from "react";
import {
  getMarketplaceAttributionParams,
  hasMarketplaceAttributionParams,
  MARKETPLACE_ATTRIBUTION_PARAMS,
} from "@/utils/functions/marketplaceAttribution";
import {
  persistMarketplaceAttribution,
  readStoredMarketplaceAttribution,
} from "@/utils/functions/marketplaceAttributionSession";

type SegmentCampaignContext = {
  source?: string;
  medium?: string;
  name?: string;
};

type SegmentMiddlewarePayload = {
  obj: {
    type?: string;
    context?: {
      campaign?: SegmentCampaignContext;
      [key: string]: unknown;
    };
    properties?: Record<string, unknown>;
  };
};

type SegmentAnalyticsWithMiddleware = {
  addSourceMiddleware?: (
    middleware: (args: {
      payload: SegmentMiddlewarePayload;
      next: (payload: SegmentMiddlewarePayload) => void;
    }) => void,
  ) => void;
};

declare global {
  interface Window {
    __edxMarketplaceAttributionMiddlewareRegistered?: boolean;
  }
}

function getSegmentAnalytics(): SegmentAnalyticsWithMiddleware | undefined {
  return (window as Window & { analytics?: SegmentAnalyticsWithMiddleware })
    .analytics;
}

function restoreAttributionToUrl(stored: URLSearchParams): boolean {
  if (typeof window === "undefined" || !stored.toString()) {
    return false;
  }

  const url = new URL(window.location.href);
  let changed = false;

  MARKETPLACE_ATTRIBUTION_PARAMS.forEach((key) => {
    const value = stored.get(key);
    if (value && !url.searchParams.get(key)) {
      url.searchParams.set(key, value);
      changed = true;
    }
  });

  if (!changed) {
    return false;
  }

  window.history.replaceState(
    null,
    "",
    `${url.pathname}${url.search}${url.hash}`,
  );
  window.dispatchEvent(new PopStateEvent("popstate"));
  return true;
}

function toSegmentCampaign(
  attribution: URLSearchParams,
): SegmentCampaignContext | null {
  const source = attribution.get("utm_source") || undefined;
  const medium = attribution.get("utm_medium") || undefined;
  const name = attribution.get("utm_campaign") || undefined;

  if (!source && !medium && !name) {
    return null;
  }

  return { source, medium, name };
}

function registerSegmentAttributionMiddleware(
  attribution: URLSearchParams,
): void {
  const campaign = toSegmentCampaign(attribution);
  const analytics = getSegmentAnalytics();
  if (!campaign || typeof window === "undefined" || !analytics) {
    return;
  }

  if (typeof analytics.addSourceMiddleware !== "function") {
    return;
  }

  if (window.__edxMarketplaceAttributionMiddlewareRegistered) {
    return;
  }

  window.__edxMarketplaceAttributionMiddlewareRegistered = true;

  analytics.addSourceMiddleware(({ payload, next }) => {
    const storedCampaign =
      toSegmentCampaign(readStoredMarketplaceAttribution()) || campaign;

    if (payload.obj.type === "page" || payload.obj.type === "track") {
      payload.obj.context = payload.obj.context || {};
      if (!payload.obj.context.campaign) {
        payload.obj.context.campaign = storedCampaign;
      }

      payload.obj.properties = {
        ...payload.obj.properties,
        ...(storedCampaign.source ? { utm_source: storedCampaign.source } : {}),
        ...(storedCampaign.medium ? { utm_medium: storedCampaign.medium } : {}),
        ...(storedCampaign.name ? { utm_campaign: storedCampaign.name } : {}),
      };
    }

    next(payload);
  });
}

/**
 * Keeps marketplace UTMs available for the browser session:
 * - persists landing UTMs to cookie + sessionStorage
 * - restores them onto the URL when redirects/CRO drop query params
 * - injects campaign context into later Segment page/track events
 */
export default function MarketplaceAttributionPersistence() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const lastSerializedAttribution = useRef<string>("");

  useEffect(() => {
    const urlAttribution = getMarketplaceAttributionParams(
      searchParams?.toString() ?? window.location.search,
    );

    if (hasMarketplaceAttributionParams(urlAttribution)) {
      const serialized = urlAttribution.toString();
      if (serialized !== lastSerializedAttribution.current) {
        persistMarketplaceAttribution(urlAttribution);
        lastSerializedAttribution.current = serialized;
      }
      registerSegmentAttributionMiddleware(urlAttribution);
      return;
    }

    const stored = readStoredMarketplaceAttribution();
    if (!stored.toString()) {
      return;
    }

    restoreAttributionToUrl(stored);
    registerSegmentAttributionMiddleware(stored);
  }, [pathname, searchParams]);

  return null;
}
